import {
  type AlmanacGroup,
  useAlmanacOptions,
} from "portal/components/categoryTemplates/useAlmanacOptions";
import {
  Category,
  Role,
  roleFromJSON,
  roleToJSON,
} from "protos/robot_syncer/plant_profile";
import {
  categoryHasAlmanac,
  defaultMostSimilarTo,
  FIXED_CATEGORY_DEFAULT_ROLES,
  type PlantProfileRoleOption,
  sortCategoryOptions,
} from "portal/utils/categoryCollection/category";
import {
  categoryOwnershipFromPlantProfile,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  type CreateCategoryFormValues,
  createCategoryValidationSchema,
} from "../createCategory/useCreateCategoryForm";
import { FormikConfig } from "formik";
import { i18n as I18n } from "i18next";
import { resolveAlmanacCategory } from "portal/components/categoryTemplates/createCategory/resolveAlmanacCategory";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type ConvertCategoryDraft = CreateCategoryFormValues & {
  id: string;
  chipIds: string[];
  customerId?: string;
  carbon?: boolean;
  /** English fixed name at conversion start (stable if the user renames). */
  originalFixedName: string;
};

type ResolveResult =
  | { ok: true; category: Category; role: Role }
  | { ok: false; field: keyof CreateCategoryFormValues; message: string };

const buildConvertCategoryDrafts = (
  unsavedPlantProfile: UnsavedPlantProfile,
  defaultAlmanacId: string,
  i18n: I18n
): ConvertCategoryDraft[] =>
  [...unsavedPlantProfile.categories]
    .filter((category) => !categoryHasAlmanac(category)) // only look for categories that have no discernable almanac
    .sort(sortCategoryOptions(i18n))
    .map((category) => {
      const defaultRole = FIXED_CATEGORY_DEFAULT_ROLES[category.name]!; // get role based on fixed name
      return {
        id: category.id,
        chipIds: [...category.chipIds],
        ...(category.customerId ? { customerId: category.customerId } : {}),
        ...(category.carbon ? { carbon: true } : {}),
        originalFixedName: category.name,
        name: category.name,
        role: roleToJSON(defaultRole),
        sourceAlmanacId: defaultAlmanacId,
        mostSimilarTo: defaultMostSimilarTo(
          category.name,
          roleToJSON(defaultRole) as PlantProfileRoleOption
        ),
      };
    });

type UseConvertFixedCategoriesFormResult = {
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  isLoading: boolean;
  initialDrafts: ConvertCategoryDraft[];
  validationSchema: FormikConfig<CreateCategoryFormValues>["validationSchema"];
  resolveDraft: (draft: ConvertCategoryDraft) => ResolveResult;
};

export const useConvertFixedCategoriesForm = (
  unsavedPlantProfile: UnsavedPlantProfile,
  customerUuid: string | undefined
): UseConvertFixedCategoriesFormResult => {
  const { t, i18n } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } = useAlmanacOptions(
    customerUuid ?? unsavedPlantProfile.customerId
  );

  const initialDrafts = useMemo(
    () =>
      buildConvertCategoryDrafts(unsavedPlantProfile, defaultAlmanacId, i18n),
    [unsavedPlantProfile, defaultAlmanacId, i18n]
  );

  const validationSchema = useMemo(
    () => createCategoryValidationSchema(t),
    [t]
  );

  const resolveDraft = (draft: ConvertCategoryDraft): ResolveResult => {
    const resolved = resolveAlmanacCategory(
      almanacGroups,
      draft.sourceAlmanacId,
      draft.mostSimilarTo,
      t
    );
    if (!resolved.ok) {
      return resolved;
    }
    const ownership =
      draft.carbon || draft.customerId
        ? {
            ...(draft.carbon ? { carbon: true } : {}),
            ...(draft.customerId ? { customerId: draft.customerId } : {}),
          }
        : categoryOwnershipFromPlantProfile(unsavedPlantProfile);
    return {
      ok: true,
      role: roleFromJSON(draft.role),
      category: {
        id: draft.id,
        name: draft.name.trim(),
        chipIds: draft.chipIds,
        almanac: resolved.almanac,
        ...ownership,
      },
    };
  };

  return {
    almanacGroups,
    defaultAlmanacId,
    isLoading,
    initialDrafts,
    validationSchema,
    resolveDraft,
  };
};
